import { ChatMarkdown } from "@/core/components/chat-markdown";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { EnrichedResult } from "@/payroll/editor/types";
import { CalculationResult } from "./calculation-result";

// The agent writes its question in markdown, so rendering it as plain text
// leaves the reader looking at raw ** around the part that matters most.
const Prose = ({ text, muted }: { text: string; muted?: boolean }) => (
  <div className={cn({ "text-subtle": muted })}>
    <ChatMarkdown>{text}</ChatMarkdown>
  </div>
);

export const EditorResult = ({
  result,
  onReply,
  onWriteOwn,
  disabled,
}: {
  result: EnrichedResult;
  /** Sends this answer as it stands. */
  onReply: (reply: string) => void;
  /** Puts the reader in the composer to answer in their own words. */
  onWriteOwn: () => void;
  disabled: boolean;
}) => {
  if (result.kind === "clarification")
    return (
      <Stack
        gap={0}
        align="stretch"
        direction="column"
        className="flex flex-col gap-2"
      >
        <h3 className="text-xs font-medium tracking-widest text-subtle uppercase">
          One thing first
        </h3>
        <Prose
          text={result.question || "Add more detail about the change you need."}
        />
        {!!result.reason && <Prose text={result.reason} muted />}
        {/* Clicking one sends it. The reader has already made the decision
            the question asked for, and making them press send again is a
            second decision about the same thing. The way out is said rather
            than implied, because a row of buttons reads as the whole set of
            answers available. */}
        <Stack
          gap={0}
          align="stretch"
          direction="row"
          className="flex flex-wrap items-center gap-2"
        >
          {result.choices?.map((choice, index) => (
            <Button
              key={index}
              size="xs"
              variant="outline"
              className="h-auto text-left whitespace-normal"
              disabled={disabled}
              onClick={() => onReply(choice)}
            >
              {choice}
            </Button>
          ))}
          {!!result.choices?.length && (
            <Button
              size="xs"
              variant="link"
              className="h-auto p-0"
              disabled={disabled}
              onClick={onWriteOwn}
            >
              Answer in your own words
            </Button>
          )}
        </Stack>
      </Stack>
    );
  if (result.kind === "saved_changes")
    return (
      <Stack
        gap={0}
        align="stretch"
        direction="column"
        className="flex flex-col gap-1.5"
      >
        <h3 className="text-xs font-medium tracking-widest text-subtle uppercase">
          {result.applied ? "Saved to your proposal" : "Not saved"}
        </h3>
        {result.summary && <p>{result.summary}</p>}
        {result.error && (
          <p>
            Review the proposed rules. Ask the agent to correct the unresolved
            problem.
          </p>
        )}
        {/* Governance says what it found. Replacing that with "ask the agent to
            explain the remaining setup changes" threw the answer away and left
            the reader a chore. */}
        {result.governanceMessages.map((warning, index) => (
          <p key={index}>{warning}</p>
        ))}
      </Stack>
    );
  // Validation runs several times per request while the agent works, and a
  // failed intermediate attempt is normal. The outcome the owner needs is the
  // saved-changes card, the calculation comparison, and the written answer.
  if (result.kind === "validation") return null;
  // A figure check is the same kind of thing: the agent reading back a specific
  // number part way through, which it then explains in its written answer. The
  // owner gets the outcome, not the working.
  if (result.kind === "figure_check") return null;
  if (result.kind === "calculation_comparison")
    return <CalculationResult result={result} />;
  return null;
};
